import React, { useState, useEffect } from "react";
import "./App.css";
import photo from "./photo/img.png";
import WebFont from "webfontloader";
import { fonts } from "./fontsList";

function randomColor() {
  return (
    "#" +
    Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, "0")
  );
}

function App() {
  const [text, setText] = useState("Hello, TextFX.css!");
  const [color1, setColor1] = useState("#f97316");
  const [color2, setColor2] = useState("#22c55e");
  const [angle, setAngle] = useState(45);
  const [fontSize, setFontSize] = useState(48);

  const [strokeWidth, setStrokeWidth] = useState(1);
  const [strokeColor, setStrokeColor] = useState("#000000");

  const [shadowX, setShadowX] = useState(0);
  const [shadowY, setShadowY] = useState(0);
  const [shadowBlur, setShadowBlur] = useState(16);
  const [shadowColor, setShadowColor] = useState("#f97316");

  const [fontFamily, setFontFamily] = useState("system");
  const [bgMode, setBgMode] = useState("dark");

  // Keep track of loaded fonts to avoid reloading
  const [loadedFonts, setLoadedFonts] = useState({});

  // Derived values
  const gradient = `linear-gradient(${angle}deg, ${color1}, ${color2})`;

  // build CSS font-family string for inline style
  let fontFamilyCSS =
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  if (fontFamily === "serif") {
    fontFamilyCSS = "serif";
  } else if (fontFamily !== "system") {
    // Try to find css value from fonts list
    const f = fonts.find((x) => x.name === fontFamily);
    fontFamilyCSS = f ? f.css : fontFamily;
  }

  let textShadowCSS = "none";
  if (shadowBlur > 0 || shadowX !== 0 || shadowY !== 0) {
    textShadowCSS = `${shadowX}px ${shadowY}px ${shadowBlur}px ${shadowColor}`;
  }

  const previewTextStyle = {
    fontSize: `${fontSize}px`,
    fontFamily: fontFamilyCSS,
    fontWeight: 800,
    backgroundImage: gradient,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
    WebkitTextStroke:
      strokeWidth > 0 ? `${strokeWidth}px ${strokeColor}` : "0px transparent",
    textShadow: textShadowCSS,
    margin: 0,
    wordBreak: "break-word",
  };

  const previewAreaClass = `preview-area preview-${bgMode}`;

  const cssString = [
    ".textfx {",
    `  font-size: ${fontSize}px;`,
    `  font-family: ${fontFamilyCSS};`,
    "  font-weight: 800;",
    `  background-image: ${gradient};`,
    "  -webkit-background-clip: text;",
    "  background-clip: text;",
    "  -webkit-text-fill-color: transparent;",
    strokeWidth > 0
      ? `  -webkit-text-stroke: ${strokeWidth}px ${strokeColor};`
      : null,
    textShadowCSS !== "none" ? `  text-shadow: ${textShadowCSS};` : null,
    "}",
  ]
    .filter(Boolean)
    .join("\n");

  // Load default fonts we want preloaded (optional)
  useEffect(() => {
    // preload a few common fonts to make UI snappier
    WebFont.load({
      google: {
        families: ["Poppins:300,400,600,800", "Roboto:300,400,700"],
      },
    });
    setLoadedFonts((s) => ({ ...s, Poppins: true, Roboto: true }));
  }, []);

  const handleFontChange = (e) => {
    const chosen = e.target.value;
    setFontFamily(chosen);

    if (chosen === "system" || chosen === "serif") return;

    // if font not loaded yet, load via WebFont
    if (!loadedFonts[chosen]) {
      const f = fonts.find((x) => x.name === chosen);
      const familyStr = f
        ? `${f.name}:${f.weights || "400,700"}`
        : `${chosen}:400,700`;

      WebFont.load({
        google: {
          families: [familyStr],
        },
        active: function () {
          setLoadedFonts((s) => ({ ...s, [chosen]: true }));
        },
      });
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cssString);
      alert("CSS copied to clipboard!");
    } catch (err) {
      alert("Could not copy. Please select and copy manually.");
    }
  };

  const handleRandomize = () => {
    const randomFontSizes = [32, 40, 48, 56, 64, 72];
    const randomSize =
      randomFontSizes[Math.floor(Math.random() * randomFontSizes.length)];

    const c1 = randomColor();
    const c2 = randomColor();

    setColor1(c1);
    setColor2(c2);
    setAngle(Math.floor(Math.random() * 360));
    setFontSize(randomSize);
    setStrokeWidth(Math.floor(Math.random() * 4)); // 0–3
    setStrokeColor(randomColor());
    setShadowX(0);
    setShadowY(0);
    setShadowBlur(12 + Math.floor(Math.random() * 16));
    setShadowColor(c1);

    const fontOptions = fonts.map((f) => f.name);
    setFontFamily(fontOptions[Math.floor(Math.random() * fontOptions.length)]);
  };

  return (
    <div className="app-root">
      <div className="container-fluid h-100 py-3">
        <div className="row h-100">
          {/* LEFT: Controls */}
          <div className="col-md-4 mb-3 d-flex flex-column">
            <div className="card bg-dark bg-opacity-75 text-light shadow-lg flex-fill">
              <div className="card-body d-flex flex-column gap-2">
                <h1 className="h4 mb-1">TextFX.css</h1>
                <p className="text-muted small mb-2">
                  Gradient + stroke + glow CSS text generator (Bootstrap).
                </p>

                {/* Text */}
                <div className="mb-2">
                  <label htmlFor="textInput" className="form-label small mb-1">
                    Text
                  </label>
                  <input
                    id="textInput"
                    type="text"
                    className="form-control form-control-sm"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                </div>

                {/* Gradient colors */}
                <div className="mb-2">
                  <label className="form-label small mb-1">
                    Gradient colors
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={color1}
                      onChange={(e) => setColor1(e.target.value)}
                      title="Color 1"
                    />
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={color2}
                      onChange={(e) => setColor2(e.target.value)}
                      title="Color 2"
                    />
                  </div>
                </div>

                {/* Angle */}
                <div className="mb-2">
                  <label htmlFor="angle" className="form-label small mb-1">
                    Gradient angle: <strong>{angle}°</strong>
                  </label>
                  <input
                    id="angle"
                    type="range"
                    className="form-range"
                    min="0"
                    max="360"
                    value={angle}
                    onChange={(e) => setAngle(Number(e.target.value))}
                  />
                </div>

                {/* Font size */}
                <div className="mb-2">
                  <label htmlFor="fontSize" className="form-label small mb-1">
                    Font size: <strong>{fontSize}px</strong>
                  </label>
                  <input
                    id="fontSize"
                    type="range"
                    className="form-range"
                    min="24"
                    max="96"
                    value={fontSize}
                    onChange={(e) => setFontSize(Number(e.target.value))}
                  />
                </div>

                {/* Stroke (slider) */}
                <div className="mb-2">
                  <label className="form-label small mb-1">
                    Stroke width: <strong>{strokeWidth}px</strong>
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="0"
                    max="10"
                    value={strokeWidth}
                    onChange={(e) => setStrokeWidth(Number(e.target.value))}
                  />
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <span className="form-text small text-muted">
                      Set 0 for no stroke
                    </span>
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={strokeColor}
                      onChange={(e) => setStrokeColor(e.target.value)}
                      title="Stroke color"
                    />
                  </div>
                </div>

                {/* Shadow (sliders) */}
                <div className="mb-2">
                  <label className="form-label small mb-1">Shadow (glow)</label>

                  <label className="form-label small mb-0">
                    X offset: <strong>{shadowX}px</strong>
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="-50"
                    max="50"
                    value={shadowX}
                    onChange={(e) => setShadowX(Number(e.target.value))}
                  />

                  <label className="form-label small mb-0 mt-1">
                    Y offset: <strong>{shadowY}px</strong>
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="-50"
                    max="50"
                    value={shadowY}
                    onChange={(e) => setShadowY(Number(e.target.value))}
                  />

                  <label className="form-label small mb-0 mt-1">
                    Blur: <strong>{shadowBlur}px</strong>
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="0"
                    max="50"
                    value={shadowBlur}
                    onChange={(e) => setShadowBlur(Number(e.target.value))}
                  />

                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <span className="form-text small text-muted">
                      Blur 0 removes glow
                    </span>
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={shadowColor}
                      onChange={(e) => setShadowColor(e.target.value)}
                      title="Shadow color"
                    />
                  </div>
                </div>

                {/* Font family (dynamic list) */}
                <div className="mb-2">
                  <label
                    htmlFor="fontFamily"
                    className="form-label small mb-1"
                  >
                    Font family
                  </label>
                  <select
                    id="fontFamily"
                    className="form-select form-select-sm"
                    value={fontFamily}
                    onChange={handleFontChange}
                  >
                    <option value="system">System default</option>
                    <option value="serif">Serif</option>
                    {fonts.map((f) => (
                      <option key={f.name} value={f.name}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Preview + CSS */}
          <div className="col-md-8 d-flex flex-column gap-3">
            {/* Preview card */}
            <div className="card bg-dark bg-opacity-75 text-light shadow-lg flex-fill">
              <div className="card-body d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted">Preview</span>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className={`btn btn-outline-light ${
                        bgMode === "dark" ? "active" : ""
                      }`}
                      onClick={() => setBgMode("dark")}
                    >
                      Dark
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-light ${
                        bgMode === "light" ? "active" : ""
                      }`}
                      onClick={() => setBgMode("light")}
                    >
                      Light
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-light ${
                        bgMode === "gradient" ? "active" : ""
                      }`}
                      onClick={() => setBgMode("gradient")}
                    >
                      Gradient
                    </button>
                  </div>
                </div>

                <div className={previewAreaClass}>
                  <h2 style={previewTextStyle}>
                    {text || "Hello, TextFX.css!"}
                  </h2>
                </div>
              </div>
            </div>

            {/* CSS code card */}
            <div className="card bg-dark bg-opacity-75 text-light shadow-lg">
              <div className="card-body d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted">
                    Generated CSS (.textfx)
                  </span>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-light btn-sm"
                      type="button"
                      onClick={handleRandomize}
                    >
                      Randomize
                    </button>
                    <button
                      className="btn btn-success btn-sm"
                      type="button"
                      onClick={handleCopy}
                    >
                      Copy CSS
                    </button>
                  </div>
                </div>
                <textarea
                  className="form-control form-control-sm code-textarea"
                  rows={8}
                  readOnly
                  value={cssString}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer className="bg-dark text-light mt-auto py-5">
        <div className="container">
          <div className="row align-items-center mb-4" id="about">
            <div className="col-md-3 text-center mb-3 mb-md-0">
              <div
                className="rounded-circle overflow-hidden mx-auto"
                style={{ width: "150px", height: "150px", backgroundColor: "#555" }}
              >
                <img
                  src={photo}
                  alt="Pavithran M"
                  className="img-fluid h-200 w-100 object-fit-cover"
                />
              </div>
            </div>
            <div className="col-md-9">
              <h4 className="fw-bold">About Me</h4>
              <p className="mb-0">
                Pavithran M, a pre-final year student from CEG, Anna University. A budding engineer who is willing to do creative projects and combine technology with imagination to build meaningful tools that simplify life.
              </p>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3" id="features">
              <h5>Features</h5>
              <ul className="list-unstyled">
                <li>✔ Live gradient text preview </li>
                <li>✔ Stroke & glow sliders with instant output </li>
                <li>✔ Background preview modes (Dark / Light / Gradient)  </li>
                <li>✔ Custom fonts & full styling controls</li>
                <li>✔ Random style generator </li>
              </ul>
            </div>
            <div className="col-md-4 mb-3" id="contact">
              <h5>Contact</h5>
              <ul className="list-unstyled">
                <li>Email: pavicontact14@gmail.com</li>
              </ul>
            </div>
          </div>

          <hr className="border-light" />
          <p className="text-center mb-0 small">© 2025 TextCss. Built with ❤️ using React & Bootstrap by Pavithran M.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
